import React, { useState, useEffect } from 'react';
import './UsersPage.css';
import { authFetch } from '../api';

const PER_PAGE = 50;
const allowedSortFields = [
  'id',
  'firstName',
  'lastName',
  'language',
  'euStatus',
  'verified',
  'dateOfBirth',
  'totalBalance',
] ;

const parseResult = (result) => {
  let list = [];
  let totalItems = 0;
  let pageCount = 1;
  if (result && Array.isArray(result.results)) {
    list = result.results;
    totalItems = result.total ?? result.count ?? result.resultsCount ?? list.length;
    if (result.pageCount) {
      pageCount = result.pageCount;
    } else if (result.total && result.currentCount) {
      pageCount = Math.max(1, Math.ceil(result.total / result.currentCount));
    }
  } else if (Array.isArray(result)) {
    list = result;
    totalItems = result.length;
  } else if (result && Array.isArray(result.data)) {
    list = result.data;
    totalItems = result.total ?? result.count ?? result.totalCount ?? list.length;
    if (result.total) {
      pageCount = Math.max(1, Math.ceil(result.total / PER_PAGE));
    }
  } else if (result && Array.isArray(result.items)) {
    list = result.items;
    totalItems = result.count ?? result.total ?? result.totalCount ?? list.length;
    if (result.count) {
      pageCount = Math.max(1, Math.ceil(result.count / PER_PAGE));
    }
  } else if (result) {
    list = [result];
    totalItems = 1;
  }
  return { list, totalItems, pageCount };
};

const UsersPage = ({ onUserClick }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [tabCounts, setTabCounts] = useState({ all: 0, verified: 0, unverified: 0 });
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('ASC');


  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const url = new URL('https://demo.api.da-capital.io/v1/users');
        url.searchParams.set('page', page);
        url.searchParams.set('sortBy', sortBy);
        url.searchParams.set('sortOrder', sortOrder);
        if (activeTab === 'verified') {
          url.searchParams.set('filters', 'verified=true');
        } else if (activeTab === 'unverified') {
          url.searchParams.set('filters', 'verified=false');
        }

        const resp = await authFetch(url.toString(), {
          method: 'GET',
        });
        if (!resp.ok) {
          throw new Error(`HTTP error ${resp.status}`);
        }
        const result = await resp.json();
        const { list, totalItems, pageCount } = parseResult(result);
        if (pageCount) {
          setTotalPages(pageCount);
        }
        setUsers(list);
        setTabCounts((prev) => ({
          ...prev,
          [activeTab]: totalItems || list.length,
        }));
        setError(null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page, sortBy, sortOrder, activeTab]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const makeRequest = async (filter) => {
          const url = new URL('https://demo.api.da-capital.io/v1/users');
          url.searchParams.set('page', 1);
          url.searchParams.set('filters', filter);
          const resp = await authFetch(url.toString(), { method: 'GET' });
          if (!resp.ok) {
            throw new Error(`HTTP error ${resp.status}`);
          }
          const result = await resp.json();
          const { totalItems } = parseResult(result);
          return totalItems || 0;
        };

        const [verifiedTotal, unverifiedTotal] = await Promise.all([
          makeRequest('verified=true'),
          makeRequest('verified=false'),
        ]);

        setTabCounts((prev) => ({
          ...prev,
          verified: verifiedTotal,
          unverified: unverifiedTotal,
        }));
      } catch (e) {
        // ignore count loading errors
      }
    };

    fetchCounts();
  }, []);

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'firstName', label: 'Name' },
    { key: 'lastName', label: 'Surname' },
    { key: 'language', label: 'Language' },
    { key: 'euStatus', label: 'EU' },
    { key: 'verified', label: 'Verified' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'referral', label: 'Referral' },
    { key: 'totalBalance', label: 'Balance' },
  ];

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return parseFloat(num).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatField = (key, value) => {
    switch (key) {
      case 'language':
        if (value === 'RU') return '🇷🇺';
        if (value === 'EN') return '🇬🇧';
        if (value === 'PL') return '🇵🇱';
        return value || '';
      case 'euStatus':
        return value ? '✅' : '❌';
      case 'verified':
        return value ? '✅' : '❌';
      case 'dateOfBirth':
        return value || '';
      case 'referral':
        return value ? value : '❌';
      case 'totalBalance':
        return formatNumber(value);
      default:
        return value;
    }
  };

  const handleSort = (key) => {
    if (!allowedSortFields.includes(key)) return;
    if (sortBy === key) {
      setSortOrder((o) => (o === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const displayUsers = users;
  const displayColumns = columns;


  return (
    <div className="users-container">
      <div className="tabs">
        <div
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('all');
            setPage(1);
          }}
        >
          {`ALL USERS | ${tabCounts.all}`}
        </div>
        <div
          className={`tab ${activeTab === 'verified' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('verified');
            setPage(1);
          }}
        >
          {`VERIFIED | ${tabCounts.verified}`}
        </div>
        <div
          className={`tab ${activeTab === 'unverified' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('unverified');
            setPage(1);
          }}
        >
          {`UNVERIFIED | ${tabCounts.unverified}`}
        </div>
      </div>
      {loading ? (
        <div className="loading">Loading...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <table className="users-table">
            <thead>
              <tr>
                {displayColumns.map((c) => (
                  <th
                    key={c.key}
                    className={`${allowedSortFields.includes(c.key) ? 'sortable' : ''} col-${c.key}`}
                    onClick={() => handleSort(c.key)}
                  >
                    {c.label}
                    {sortBy === c.key ? (sortOrder === 'ASC' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayUsers.map((u, idx) => (
                <tr key={idx} className="user-row" onClick={() => onUserClick && onUserClick(u.id)}>
                  {displayColumns.map((c) => (
                    <td key={c.key} className={`col-${c.key}`}>{formatField(c.key, u[c.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              &lt;
            </button>
            <span className="page-num">{`${page} / ${totalPages}`}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              &gt;
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default UsersPage;
